'use client';
import React from 'react';
import AppLayout from '@/components/AppLayout';
import { Mail, Globe, Clock, MessageSquare, AlertCircle } from 'lucide-react';

export default function ContactUsPage() {
  return (
    <AppLayout activeRoute="/contact-us">
      <div className="max-w-3xl mx-auto fade-in">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
            <MessageSquare size={20} className="text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Contact Us</h1>
            <p className="text-sm text-muted-foreground mt-0.5">We would love to hear from you</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Intro */}
          <div className="card-base p-6 text-sm text-muted-foreground leading-relaxed">
            <p>
              For inquiries, support, partnerships, or business-related questions, please contact us using the details below.
            </p>
          </div>

          {/* Contact Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="card-base p-5 flex items-start gap-4">
              <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                <Mail size={18} className="text-amber-400" />
              </div>
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">Customer Support</p>
                <a
                  href="mailto:sales@propdatax.com"
                  className="text-sm text-amber-400 hover:text-amber-300 transition-colors font-medium"
                >
                  sales@propdatax.com
                </a>
              </div>
            </div>

            <div className="card-base p-5 flex items-start gap-4">
              <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                <Globe size={18} className="text-amber-400" />
              </div>
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">Website</p>
                <a
                  href="https://propdatax.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-amber-400 hover:text-amber-300 transition-colors font-medium"
                >
                  https://propdatax.com
                </a>
              </div>
            </div>

            <div className="card-base p-5 flex items-start gap-4 sm:col-span-2">
              <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                <Clock size={18} className="text-amber-400" />
              </div>
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">Business Hours</p>
                <p className="text-sm text-foreground font-medium">Monday to Saturday</p>
                <p className="text-sm text-muted-foreground">10:00 AM – 7:00 PM IST</p>
              </div>
            </div>
          </div>

          {/* Response Time */}
          <div className="card-base p-6">
            <h2 className="text-base font-semibold text-foreground mb-3">Support Response Time</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              We aim to respond to all support requests within <span className="text-foreground font-medium">24–48 business hours</span>.
            </p>
          </div>

          {/* Important Note */}
          <div className="card-base p-6 border-amber-500/20">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle size={16} className="text-amber-400 shrink-0" />
              <h2 className="text-base font-semibold text-foreground">Important Note</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-3">Please include relevant details such as:</p>
            <ul className="space-y-2">
              {[
                'Registered email address',
                'Transaction ID (if applicable)',
                'Screenshots or issue details',
              ]?.map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-sm text-muted-foreground mt-3">This helps us resolve your queries faster.</p>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
